import { useMemo } from 'react';
import { CSSProperties } from 'styled-components';

import {
  BarChartItem,
  DoughnutChartItem,
  VfoDoughnutChart,
  WidgetContainer,
} from '@/features/vfo/components';
import { getRecentActivity, getTaskHealth, getTasksKpi } from '@/features/vfo/helpers';
import { ApiDashboardResponseV2 } from '@/shared/api';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import {
  ChartsWrapper,
  NoJobsWrapper,
  TaskStatisticsWrapper,
  VfoBarChartStyled,
  WidgetContainerStyled,
} from './styled';

type AnalyticsOverviewProps = {
  analyticsData?: ApiDashboardResponseV2;
  style?: CSSProperties;
};

export const AnalyticsOverview = ({ analyticsData, style }: AnalyticsOverviewProps) => {
  const { t } = useTranslation('features/vfo', { keyPrefix: 'analytics' });

  const tasksKpi = getTasksKpi(analyticsData);

  const recentActivity = useMemo<BarChartItem[]>(() => {
    const activity = getRecentActivity(analyticsData);
    return [
      {
        color: COLORS.colorAssetDistributionCharts[0],
        label: t('newTasks'),
        value: activity.newTasks,
      },
      {
        color: COLORS.colorAssetDistributionCharts[2],
        label: t('newDocuments'),
        value: activity.newDocuments,
      },
    ];
  }, [analyticsData]);

  const taskHealth = useMemo<DoughnutChartItem[]>(() => {
    const healthDistribution = getTaskHealth(analyticsData);
    if (healthDistribution.totalTasks === 0) return [];

    return [
      {
        color: COLORS.colorAssetDistributionCharts[0],
        label: t('activeTasks'),
        value: (healthDistribution.active / healthDistribution.totalTasks) * 100,
        id: '1',
      },
      {
        color: COLORS.colorAssetDistributionCharts[2],
        label: t('done'),
        value: (healthDistribution.done / healthDistribution.totalTasks) * 100,
        id: '2',
      },
    ];
  }, [analyticsData]);

  return (
    <ChartsWrapper style={style}>
      <TaskStatisticsWrapper>
        <WidgetContainerStyled
          size="small"
          icon="list-checks"
          title={`${tasksKpi.totalTasks ?? 0} ${t('totalTasks')}`}
          titleClassName="titleWrapper"
        />
        <WidgetContainerStyled
          size="small"
          icon="check-square-offset"
          title={`${tasksKpi.overdueTasks ?? 0} ${t('overdueTasks')}`}
          titleClassName="titleWrapper"
        />
        <WidgetContainerStyled
          size="small"
          icon="list-bullets"
          title={`${tasksKpi.tasksWithoutAssignee ?? 0} ${t('tasksWithoutAssignee')}`}
          titleClassName="titleWrapper"
        />
      </TaskStatisticsWrapper>
      {tasksKpi.totalTasks === 0 ? (
        <WidgetContainer style={{ flex: 1 }} title={t('tasksHealth')}>
          <NoJobsWrapper>
            <Icon style={{ color: COLORS.systemDark100 }} name="chart-donut" size="s44" />
            <b>{t('noTasksYet')}</b>
          </NoJobsWrapper>
        </WidgetContainer>
      ) : (
        <VfoDoughnutChart
          type="percent"
          style={{ flex: 1 }}
          title={t('tasksHealth')}
          items={taskHealth}
          legendSeparator
        />
      )}

      <VfoBarChartStyled
        subTitle={t('sevenDays')}
        title={t('recentActivity')}
        items={recentActivity}
      />
    </ChartsWrapper>
  );
};
